import * as styledElements from "./BoardCardStyles";
import { BoardNickname } from "./BoardCardStyles";
import Icon from "../../common/Icon";
import {
  dateStringToHumanReadableDate,
  datetimeStringToHumanReadableDate,
  formatCurrency,
  formatShipmentLine,
} from "../../../utils/valueFormatting";
import CircleBadge from "../../common/CircleBadge";
import React, { ReactNode, useState } from "react";
import ShareBoard from "../../ShareBoard";
import { useAppSelector } from "../../../utils/hooks/store";
import ProjectOwner from "../../ShareBoard/ProjectOwner";
import {
  Board,
  boardHasBeenPaidFor,
  OrderSummary,
  ProductionStatus,
  QuoteState,
  Shipment,
} from "../../../reducers/uploadedBoard";
import BoardCardAlerts from "../../ReviewBoardFlow/BoardAlerts";
import { useDeleteBoard } from "../../../utils/hooks/commonly-stored/uploaded-board/deleteBoard";
import Link from "next/link";
import getBoardLink from "../../../utils/links/boards";
import { ADMIN_GROUP, ENGINEER_GROUP } from "../../../utils/group";

export function getBoardStateForDisplay(board: Board): string {
  if (board.quote.state == QuoteState.ORDER_RECEIVED) {
    return board.order_summary?.state || board.quote.state;
  } else if (board.quote.state == QuoteState.QUOTED) {
    if (board.quote.requested_custom_quote) {
      if (board.quote.total === null) {
        return "Awaiting custom quote";
      } else {
        return "Received custom quote";
      }
    }
  }
  return board.quote.state;
}

export default function BoardCard({
  board,
  shipments,
  hideOrderSummary,
  hideDeleteButton,
  removeBoardFromCart,
  onDelete,
  children,
  linkToInventory,
  showBottomPreview,
}: {
  board: Board;
  shipments: Shipment[] | null;
  hideOrderSummary?: boolean;
  hideDeleteButton?: boolean;
  removeBoardFromCart?: () => void;
  onDelete?: () => void;
  children?: ReactNode;
  linkToInventory?: boolean;
  showBottomPreview?: boolean;
}) {
  const { userId, groups } = useAppSelector((state) => state.auth);
  const [shareBoardId, setShareBoardId] = useState<string | null>(null);
  const isAdminOrEngineer =
    groups.includes(ADMIN_GROUP) || groups.includes(ENGINEER_GROUP);
  const boardStateToDisplay = getBoardStateForDisplay(board);
  const deleteBoard = useDeleteBoard(onDelete);

  const renderOrderSummary = (orderSummary: OrderSummary) => {
    return (
      <div>
        Order:{" "}
        <Link href={`/orders/${orderSummary.id}`} target={"_blank"}>
          {orderSummary.id}
        </Link>
      </div>
    );
  };

  return (
    <>
      <ShareBoard
        shareBoardId={shareBoardId}
        setShareBoardId={setShareBoardId}
      />
      <styledElements.BoardCard>
        <styledElements.BoardPreviewContainer>
          {board.top_preview !== null && (
            <styledElements.BoardPreview>
              {/* Don't need to worry about SEO here so disable lint */}
              {/* eslint-disable-next-line */}
              <img
                src={board.top_preview}
                alt={`${board.project_name} preview`}
              />
            </styledElements.BoardPreview>
          )}
          {showBottomPreview && board.bottom_preview !== null && (
            <styledElements.BoardPreview>
              {/* Don't need to worry about SEO here so disable lint */}
              {/* eslint-disable-next-line */}
              <img
                src={board.bottom_preview}
                alt={`${board.project_name} preview`}
              />
            </styledElements.BoardPreview>
          )}
        </styledElements.BoardPreviewContainer>
        <styledElements.BoardInfo>
          <styledElements.InfoRows>
            <styledElements.TitleInfoRow>
              <styledElements.BoardNameLink
                href={getBoardLink(board.id, linkToInventory)}
                passHref={true}
              >
                {board.project_name}
              </styledElements.BoardNameLink>
              <styledElements.BoardStatePill>
                <Icon
                  src={"/icons/dollar_card.svg"}
                  alt={boardStateToDisplay}
                  iconDimension={16}
                />
                {boardStateToDisplay}
              </styledElements.BoardStatePill>
            </styledElements.TitleInfoRow>
            {isAdminOrEngineer && board.user_id && (
              <BoardNickname>{board.internal_nickname}</BoardNickname>
            )}
            {board.user_id != userId && (
              <styledElements.ProjectOwnerRow>
                <ProjectOwner boardId={board.id} userId={board.user_id} />
              </styledElements.ProjectOwnerRow>
            )}
            <styledElements.DetailsInfoRow>
              <div>Id: {board.id}</div>
              <div>
                {`Created ${datetimeStringToHumanReadableDate(
                  board.datetime_uploaded,
                )}`}
              </div>
              {board.physical_details !== null && (
                <>
                  <div>
                    {board.physical_details.bounding_box_width.toFixed(2)} x{" "}
                    {board.physical_details.bounding_box_height.toFixed(2)}{" "}
                    {board.physical_details.bounding_box_unit}
                  </div>
                  {board.physical_details.num_layers && (
                    <div>{`Layers: ${board.physical_details.num_layers}`}</div>
                  )}
                </>
              )}
              {board.manufacturing_details.customer_specifications
                .xray_inspection && <div>X-ray inspection selected</div>}
              {board.manufacturing_details.customer_specifications.cleaning && (
                <div>Cleaning selected</div>
              )}
              {board.quote.total !== null && (
                <div>
                  Subtotal:{" "}
                  {formatCurrency(board.quote.total, board.quote.currency)}{" "}
                  {board.quote.expires_at !== null &&
                    board.quote.state !== QuoteState.ORDER_RECEIVED &&
                    `(this quotes expires on ${dateStringToHumanReadableDate(
                      board.quote.expires_at,
                    )})`}
                </div>
              )}
              {board.quote.estimated_total !== null && (
                <div>
                  Estimated subtotal:{" "}
                  {formatCurrency(
                    board.quote.estimated_total,
                    board.quote.currency,
                  )}
                </div>
              )}
              {board.manufacturing_details.customer_specifications.notes !==
                "" && <div>Has special requirements (see notes)</div>}
              {board.order_summary !== null &&
                !hideOrderSummary &&
                renderOrderSummary(board.order_summary)}
            </styledElements.DetailsInfoRow>
            <styledElements.ShipmentsRows>
              {shipments?.map((shipment) => {
                return (
                  <styledElements.ShipmentPill key={shipment.id}>
                    <div>{formatShipmentLine(shipment)}</div>
                    {shipment.production_status !==
                      ProductionStatus.QUOTING && (
                      <styledElements.ShippingState>
                        {shipment.production_status}
                      </styledElements.ShippingState>
                    )}
                    {shipment.delivery_info.shipping_rate?.tracking_url && (
                      <Link
                        href={
                          shipment.delivery_info.shipping_rate?.tracking_url
                        }
                        target={"_blank"}
                      >
                        Track shipment
                      </Link>
                    )}
                  </styledElements.ShipmentPill>
                );
              })}
            </styledElements.ShipmentsRows>
            <BoardCardAlerts
              board={board}
              shipments={shipments}
              isUpdating={false}
            />
            {children}
          </styledElements.InfoRows>
          <styledElements.ActionsRow>
            {removeBoardFromCart && (
              <CircleBadge
                iconProps={{
                  src: "/icons/trash_gray.svg",
                  alt: "remove board from cart",
                }}
                onClick={removeBoardFromCart}
              />
            )}
            {!boardHasBeenPaidFor(board) && !hideDeleteButton && (
              <CircleBadge
                iconProps={{
                  src: "/icons/trash.svg",
                  alt: "delete board",
                }}
                onClick={() => deleteBoard(board.id)}
              />
            )}
          </styledElements.ActionsRow>
        </styledElements.BoardInfo>
      </styledElements.BoardCard>
    </>
  );
}
